import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { getCategoryBySlug, categories } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const videos = useMemo(() => getVideosByCategory(slug || ''), [slug]);
  const [activeSort, setActiveSort] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const sortedVideos = useMemo(() => {
    const list = [...videos];
    switch (activeSort) {
      case 'popular':
        return list.sort((a, b) => b.views - a.views);
      case 'rating':
        return list.sort((a, b) => b.rating - a.rating);
      default:
        return list.sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
    }
  }, [videos, activeSort]);

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">分类未找到</h1>
          <p className="text-foreground-500 mb-6">该分类不存在或已被移除</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="pt-20 pb-14">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: category.name },
            ]}
          />
        </div>

        {/* Category Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500">
              <i className={`${category.icon} text-background-50 text-lg`}></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{category.name}</h1>
          </div>
          <p className="text-sm text-foreground-600 ml-13">{category.description}</p>
        </div>

        {/* Sub-category Tabs & Sort */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} mr-1 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
            {(['latest', 'popular', 'rating'] as const).map((sort) => (
              <button
                key={sort}
                onClick={() => setActiveSort(sort)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSort === sort
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {sort === 'latest' ? '最新' : sort === 'popular' ? '最热' : '高分'}
              </button>
            ))}
          </div>
        </div>

        {/* Video Grid */}
        {sortedVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-product-shop="">
            {sortedVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-video-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无视频，敬请期待</p>
          </div>
        )}
      </div>
    </main>
  );
}